import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertTriangle, Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SessionExpiryWarningProps {
  show: boolean;
  timeUntilExpiry: number;
  onExtend: () => void;
  onDismiss: () => void;
  className?: string;
  variant?: 'banner' | 'modal';
}

function formatTime(milliseconds: number): string {
  const minutes = Math.floor(milliseconds / (1000 * 60));
  const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export function SessionExpiryWarning({
  show,
  timeUntilExpiry,
  onExtend,
  onDismiss,
  className,
  variant = 'banner',
}: SessionExpiryWarningProps) {
  const [countdown, setCountdown] = useState(timeUntilExpiry);

  // Update countdown every second
  useEffect(() => {
    if (!show) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        const newValue = prev - 1000;
        return newValue <= 0 ? 0 : newValue;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [show]);

  // Reset countdown when timeUntilExpiry changes
  useEffect(() => {
    setCountdown(timeUntilExpiry);
  }, [timeUntilExpiry]);

  if (!show) return null;

  const isUrgent = countdown <= 2 * 60 * 1000; // Less than 2 minutes

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <Card
          className={cn(
            'mx-4 max-w-md p-6 shadow-2xl',
            isUrgent && 'border-destructive bg-destructive/5',
            className
          )}
        >
          <div className="flex items-start gap-4">
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-full',
                isUrgent ? 'bg-destructive/10 text-destructive' : 'bg-yellow-100 text-yellow-600'
              )}
            >
              <AlertTriangle className="size-5" />
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-semibold">Session Expiring Soon</h3>

              <p className="mt-1 text-sm text-muted-foreground">
                Your session will expire due to inactivity. Any unsaved work may be lost.
              </p>

              <div className="mt-4 flex items-center gap-2 text-sm">
                <Clock className="size-4" />
                <span className={cn('font-mono font-medium', isUrgent && 'text-destructive')}>
                  {formatTime(countdown)} remaining
                </span>
              </div>

              <div className="mt-6 flex gap-3">
                <Button onClick={onExtend} size="sm" className="flex-1">
                  Keep Me Logged In
                </Button>
                <Button onClick={onDismiss} variant="outline" size="sm">
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Banner variant
  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-40 border-b bg-card/95 shadow-sm backdrop-blur-sm',
        isUrgent ? 'border-destructive bg-destructive/5' : 'border-yellow-200 bg-yellow-50',
        className
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 p-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full',
              isUrgent ? 'bg-destructive/10 text-destructive' : 'bg-yellow-100 text-yellow-600'
            )}
          >
            <AlertTriangle className="size-4" />
          </div>

          <div>
            <p className="text-sm font-medium">
              Session expiring in{' '}
              <span className={cn('font-mono', isUrgent && 'text-destructive')}>
                {formatTime(countdown)}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              Click "Keep Me Logged In" to extend your session
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={onExtend} size="sm" variant={isUrgent ? 'destructive' : 'primary'}>
            Keep Me Logged In
          </Button>

          <Button onClick={onDismiss} size="sm" variant="ghost" className="size-8 p-0">
            <X className="size-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SessionExpiryWarning;
